import { useState, useEffect, useCallback, useRef } from 'react';
import { KeyRound, Save, Shield, ShieldOff, Plus, Trash2, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '../lib/api';
import { useToast } from '../hooks/useToast';
import { useAuth } from '../hooks/useAuth';
import { Modal } from '../components/Modal';

interface AccessCodeRow {
  id: number;
  code_type: string;
  code_value: string;
  requires_booking: boolean;
  description: string | null;
}

const EMPTY_FORM = {
  code_type: '',
  code_value: '',
  description: '',
  requires_booking: true,
};

export function AccessCodesPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const { activeMarinaId } = useAuth();
  const [codes, setCodes] = useState<AccessCodeRow[]>([]);
  const [loading, setLoading] = useState(true);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editType, setEditType] = useState('');
  const [editValue, setEditValue] = useState('');
  const [editDesc, setEditDesc] = useState('');
  const [editRequires, setEditRequires] = useState(false);
  const [saving, setSaving] = useState(false);

  const [showAdd, setShowAdd] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AccessCodeRow | null>(null);
  const [deleting, setDeleting] = useState(false);

  const toastRef = useRef(toast);
  toastRef.current = toast;

  const fetchCodes = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.getAccessCodes();
      setCodes(data.codes as unknown as AccessCodeRow[]);
    } catch (err) {
      if (err instanceof ApiError) toastRef.current.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [activeMarinaId]);

  useEffect(() => { fetchCodes(); }, [fetchCodes]);

  const startEdit = (code: AccessCodeRow) => {
    setEditingId(code.id);
    setEditType(code.code_type);
    setEditValue(code.code_value);
    setEditDesc(code.description ?? '');
    setEditRequires(code.requires_booking);
  };

  const cancelEdit = () => setEditingId(null);

  const saveEdit = async () => {
    if (!editingId || saving) return;
    if (!editType.trim() || !editValue.trim()) {
      toast.error(t('accessCodes.titleAndCodeRequired'));
      return;
    }
    setSaving(true);
    try {
      await api.updateAccessCode(editingId, {
        code_type: editType.trim(),
        code_value: editValue.trim(),
        description: editDesc.trim() || null,
        requires_booking: editRequires,
      });
      toast.success(t('accessCodes.codeUpdated'));
      setEditingId(null);
      fetchCodes();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('errors.somethingWentWrong'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    try {
      await api.deleteAccessCode(deleteTarget.id);
      toast.success(t('accessCodes.codeDeleted', { type: deleteTarget.code_type }));
      setDeleteTarget(null);
      fetchCodes();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('accessCodes.failedToDelete'));
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
      </div>
    );
  }

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/40';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">{t('accessCodes.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('accessCodes.subtitle')}</p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 transition-colors"
        >
          <Plus className="w-4 h-4" /> {t('accessCodes.addCode')}
        </button>
      </div>

      {codes.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 px-5 py-16 text-center text-sm text-gray-400">
          {t('accessCodes.noCodes')}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {codes.map((code) => {
            const isEditing = editingId === code.id;
            return (
              <div key={code.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
                  {isEditing ? (
                    <input
                      type="text"
                      value={editType}
                      onChange={(e) => setEditType(e.target.value)}
                      placeholder={t('accessCodes.codeTitlePlaceholder')}
                      className="flex-1 px-2 py-1 border border-gray-300 rounded-md text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-brand-500/40 mr-3"
                      autoFocus
                    />
                  ) : (
                    <span className="text-sm font-semibold text-gray-800 truncate">{code.code_type}</span>
                  )}
                  <div className="flex items-center gap-2 shrink-0">
                    {!isEditing ? (
                      <>
                        <button onClick={() => startEdit(code)} className="text-xs text-brand-600 hover:text-brand-700 font-medium">
                          {t('common.edit')}
                        </button>
                        <button
                          onClick={() => setDeleteTarget(code)}
                          className="p-1 rounded hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button onClick={cancelEdit} className="text-xs text-gray-500 hover:text-gray-700">{t('common.cancel')}</button>
                        <button
                          onClick={saveEdit}
                          disabled={saving}
                          className="inline-flex items-center gap-1 text-xs text-brand-600 hover:text-brand-700 font-medium disabled:opacity-50"
                        >
                          <Save className="w-3 h-3" /> {saving ? t('common.saving') : t('common.save')}
                        </button>
                      </>
                    )}
                  </div>
                </div>

                <div className="p-4 space-y-3">
                  {isEditing ? (
                    <>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">{t('accessCodes.codePassword')}</label>
                        <input
                          type="text"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          className={inputClass + ' font-mono'}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          {t('accessCodes.descriptionInstructions')}
                        </label>
                        <textarea
                          value={editDesc}
                          onChange={(e) => setEditDesc(e.target.value)}
                          rows={2}
                          className={inputClass}
                        />
                      </div>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={editRequires}
                          onChange={(e) => setEditRequires(e.target.checked)}
                          className="w-4 h-4 rounded border-gray-300 text-brand-600"
                        />
                        <span className="text-sm text-gray-700">{t('accessCodes.requiresBooking')}</span>
                      </label>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-2">
                        <KeyRound className="w-4 h-4 text-gray-400 shrink-0" />
                        <span className="text-lg font-mono font-bold text-gray-900 tracking-wider">{code.code_value}</span>
                      </div>
                      {code.description && (
                        <p className="text-xs text-gray-500 leading-relaxed">{code.description}</p>
                      )}
                      <div className="flex items-center gap-1.5 text-xs">
                        {code.requires_booking ? (
                          <span className="inline-flex items-center gap-1 text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
                            <Shield className="w-3 h-3" /> {t('accessCodes.bookingRequired')}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
                            <ShieldOff className="w-3 h-3" /> {t('accessCodes.availableToAll')}
                          </span>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add modal */}
      <AddCodeModal
        open={showAdd}
        onClose={() => setShowAdd(false)}
        onCreated={() => { setShowAdd(false); fetchCodes(); }}
      />

      {/* Delete confirmation */}
      <Modal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title={t('accessCodes.deleteCodeTitle')}
        maxWidth="max-w-sm"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center shrink-0">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-700">
                {t('common.delete')} <span className="font-semibold">"{deleteTarget?.code_type}"</span>?
              </p>
              <p className="text-xs text-gray-500 mt-1">{t('accessCodes.deleteWarning')}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={() => setDeleteTarget(null)} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
              {t('common.cancel')}
            </button>
            <button onClick={handleDelete} disabled={deleting} className="flex-1 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50">
              {deleting ? t('common.deleting') : t('common.delete')}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ── Add Code Modal ────────────────────────────────────────────────────

function AddCodeModal({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: () => void }) {
  const { t } = useTranslation();
  const toast = useToast();
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setForm(EMPTY_FORM);
  }, [open]);

  const set = (key: string, value: string | boolean) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await api.createAccessCode({
        code_type: form.code_type.trim(),
        code_value: form.code_value.trim(),
        description: form.description.trim() || null,
        requires_booking: form.requires_booking,
      });
      toast.success(t('accessCodes.codeAdded', { type: form.code_type }));
      onCreated();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : t('errors.somethingWentWrong'));
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/40';
  const labelClass = 'block text-xs font-medium text-gray-600 mb-1';

  return (
    <Modal open={open} onClose={onClose} title={t('accessCodes.addCodeTitle')} maxWidth="max-w-md">
      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label className={labelClass}>{t('accessCodes.codeTitle')}</label>
          <input
            type="text"
            value={form.code_type}
            onChange={(e) => set('code_type', e.target.value)}
            required
            maxLength={60}
            placeholder={t('accessCodes.codeTitleInputPlaceholder')}
            className={inputClass}
            autoFocus
          />
        </div>
        <div>
          <label className={labelClass}>{t('accessCodes.codeValue')}</label>
          <input
            type="text"
            value={form.code_value}
            onChange={(e) => set('code_value', e.target.value)}
            required
            maxLength={100}
            placeholder={t('accessCodes.codeValuePlaceholder')}
            className={inputClass + ' font-mono'}
          />
        </div>
        <div>
          <label className={labelClass}>{t('accessCodes.descriptionInstructions')}</label>
          <textarea
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
            rows={2}
            maxLength={300}
            placeholder={t('accessCodes.descriptionPlaceholder')}
            className={inputClass}
          />
          <p className="text-xs text-gray-400 mt-1">{t('accessCodes.aiWillInclude')}</p>
        </div>
        <label className="flex items-center gap-2 cursor-pointer pt-1">
          <input
            type="checkbox"
            checked={form.requires_booking}
            onChange={(e) => set('requires_booking', e.target.checked)}
            className="w-4 h-4 rounded border-gray-300 text-brand-600"
          />
          <span className="text-sm text-gray-700">{t('accessCodes.requiresBooking')}</span>
        </label>
        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose} className="flex-1 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
            {t('common.cancel')}
          </button>
          <button type="submit" disabled={submitting} className="flex-1 py-2 bg-brand-600 text-white rounded-lg text-sm font-medium hover:bg-brand-700 disabled:opacity-50">
            {submitting ? t('common.creating') : t('accessCodes.addCode')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
